import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { EducationEmployment } from '../../utils/formNames';
import { getFormData } from '../../utils/sessionStorage';
import { arrayToSet } from '../../utils/browserUtils';
import { FORM_HOST, TrackValues } from '../../utils/constants';
import EnrollmentForm from './EnrollmentForm';
import EnrollmentFormInput from '../FormInputs/FormInput';
import EnrollmentFormBinaryRadioInput from '../FormInputs/FormBinaryRadioInput';
import EnrollmentFormRadioBlockInput from '../FormInputs/FormRadioBlockInput';
import EnrollmentFormError from '../FormInputs/FormError';
import styles from '../../styles/EnrollmentForm.module.scss';

const formName = EducationEmployment;
const pardotFormUrl = `${FORM_HOST}/l/772833/2020-04-15/5dy4r6`;
const callbackLocations = {
  success_location: `${FORM_HOST}/l/772833/2020-07-06/7k97sb/772833/58643/education_employment_success.js`,
  error_location: `${FORM_HOST}/l/772833/2020-07-06/7k97s8/772833/58641/education_employment_error.js`,
};

const { SWE, WebDev, Data, Design, PSO, Marketing, Sales, Other } = TrackValues;
const likelySTEM = arrayToSet([SWE, WebDev, Data]);
likelySTEM[Design] = false;
likelySTEM[PSO] = false;
likelySTEM[Marketing] = false;
likelySTEM[Sales] = false;
likelySTEM[Other] = false;

const EducationEmploymentForm = ({ primaryTrack }) => {
  const { register, handleSubmit, triggerValidation, formState, watch, errors } = useForm({
    mode: 'onBlur',
    submitFocusError: false,
  });

  const previousData = getFormData(formName);

  const [hasOPT, setHasOPT] = useState(previousData.hasOPT);
  const [ineligibleSTEMOPT, setIneligibleSTEMOPT] = useState(previousData.ineligibleSTEMOPT);
  const [authorizationExpires, setAuthorizationExpires] = useState(previousData.authorizationExpires);
  const [potentialInternSeeking, setPotentialInternSeeking] = useState(previousData.potentialInternSeeking);

  const otherAuthorization = 'Other Authorization';
  const notAuthorized = 'Not Authorized';
  const mustBeAuthorized = (workAuthorization) => {
    return workAuthorization !== notAuthorized;
  };

  // Necessary because react-hook-form has a bug
  // regarding default values on required radio inputs
  useEffect(() => {
    if (previousData.professional_experience) {
      triggerValidation();
    }
  }, [previousData.professional_experience, triggerValidation]);

  return (
    <EnrollmentForm
      formName={formName}
      callbackLocations={callbackLocations}
      actionUrl={pardotFormUrl}
      formValid={formState.isValid}
      validationHandler={handleSubmit}
      saveState={{
        hasOPT,
        ineligibleSTEMOPT,
        authorizationExpires,
        potentialInternSeeking,
      }}
    >
      <legend>Employment</legend>
      <p>
        What is your current work authorization? <em>*</em>
      </p>
      <div className="blockInputs">
        <EnrollmentFormRadioBlockInput
          name="work_authorization"
          id="us-citizen"
          label="US Citizen"
          value="US Citizen"
          defaultValue={previousData.work_authorization}
          inputRef={register({ required: true, validate: mustBeAuthorized })}
          onClick={() => {
            setHasOPT(false);
            setAuthorizationExpires(false);
          }}
        />
        <EnrollmentFormRadioBlockInput
          name="work_authorization"
          id="green-card"
          label="Green Card"
          value="Green Card"
          defaultValue={previousData.work_authorization}
          inputRef={register({ required: true, validate: mustBeAuthorized })}
          onClick={() => {
            setHasOPT(false);
            setAuthorizationExpires(false);
          }}
        />
        <EnrollmentFormRadioBlockInput
          name="work_authorization"
          id="canadian-citizen"
          label="Canadian Citizen"
          value="Canadian Citizen"
          defaultValue={previousData.work_authorization}
          inputRef={register({ required: true, validate: mustBeAuthorized })}
          onClick={() => {
            setHasOPT(false);
            setAuthorizationExpires(false);
          }}
        />
        <EnrollmentFormRadioBlockInput
          name="work_authorization"
          id="h1b"
          label="H1B"
          value="H1B"
          defaultValue={previousData.work_authorization}
          inputRef={register({ required: true, validate: mustBeAuthorized })}
          onClick={() => {
            setHasOPT(false);
            setAuthorizationExpires(true);
          }}
        />
        <EnrollmentFormRadioBlockInput
          name="work_authorization"
          id="f1-opt"
          label="F1/OPT"
          value="F1 Visa/OPT"
          defaultValue={previousData.work_authorization}
          inputRef={register({ required: true, validate: mustBeAuthorized })}
          onClick={() => {
            setHasOPT(true);
            setAuthorizationExpires(false);
          }}
        />
        <EnrollmentFormRadioBlockInput
          name="work_authorization"
          id="not-authorized"
          label="Not Authorized"
          value={notAuthorized}
          defaultValue={previousData.work_authorization}
          inputRef={register({ required: true, validate: mustBeAuthorized })}
          onClick={() => {
            setHasOPT(false);
            setAuthorizationExpires(false);
            triggerValidation('work_authorization');
          }}
        />
        <EnrollmentFormRadioBlockInput
          name="work_authorization"
          id="other-authorization"
          label="Other"
          value={otherAuthorization}
          defaultValue={previousData.work_authorization}
          inputRef={register({ required: true, validate: mustBeAuthorized })}
          onClick={() => {
            setHasOPT(false);
            setAuthorizationExpires(true);
          }}
        />
        <div className="emptyBlock" />
      </div>
      <EnrollmentFormError
        errorField={errors.work_authorization}
        errorText={{
          validate:
            'Unfortunately we’re currently only able to work with individuals who are authorized to work in the US or Canada.',
        }}
        blockError
      />
      {watch('work_authorization') === otherAuthorization && (
        <>
          <EnrollmentFormInput
            name="other_authorization"
            type="text"
            label="Please indicate your work authorization:"
            placeholder="Work authorization"
            defaultValue={previousData.other_authorization}
            inputRef={register({ required: watch('work_authorization') === otherAuthorization })}
            required
            error={errors.other_authorization}
          />
          <EnrollmentFormError errorField={errors.other_authorization} />
        </>
      )}
      {hasOPT && (
        <>
          <EnrollmentFormInput
            name="opt_start_date"
            type="date"
            label="What is your OPT start date?"
            placeholder="MM/DD/YYYY"
            defaultValue={previousData.opt_start_date}
            helpText="If you are unsure, please estimate to the best of your ability."
            inputRef={register({ required: hasOPT })}
            required
            error={errors.opt_start_date}
          />
          <EnrollmentFormError errorField={errors.opt_start_date} />
          <p>
            Are you currently eligible for STEM OPT or will you be in the future? <em>*</em>
          </p>
          <div className={styles.radioInputs}>
            <EnrollmentFormBinaryRadioInput
              name="eligible_stem_opt"
              id="eligible_stem_opt"
              label="Yes"
              value="yes"
              defaultValue={previousData.eligible_stem_opt}
              inputRef={register({ required: hasOPT })}
              onClick={() => setIneligibleSTEMOPT(false)}
            />
            <EnrollmentFormBinaryRadioInput
              name="eligible_stem_opt"
              id="not_eligible_stem_opt"
              label="No"
              value="no"
              defaultValue={previousData.eligible_stem_opt}
              inputRef={register({ required: hasOPT })}
              onClick={() => setIneligibleSTEMOPT(true)}
            />
            {ineligibleSTEMOPT && (
              <p className={styles.warningText}>
                At this time we are only equipped to assist candidates who are authorized to work in the United States
                for at least two years, given how our income share agreement works. For individuals with the F-1 visa,
                STEM OPT is required to be considered for Pathrise.
              </p>
            )}
            {likelySTEM[primaryTrack] === ineligibleSTEMOPT && (
              <p className={styles.warningText}>
                {likelySTEM[primaryTrack]
                  ? "The track you've selected indicates that you may be eligible for STEM OPT if you’re majoring in a STEM major. Are you sure you are not eligible for STEM OPT?"
                  : "The track you've selected indicates that you may not be eligible for STEM OPT. Please verifiy you qualify for STEM OPT."}
                <br />
                <br />
                If you are unsure, please refer to your university’s list of eligible majors that qualify for STEM OPT.
                {'  '}
                <a href="https://studyinthestates.dhs.gov/students-determining-stem-opt-extension-eligibility">
                  Click here
                </a>{' '}
                for information.
              </p>
            )}
            <EnrollmentFormError
              errorField={errors.eligible_stem_opt}
              errorText={{
                required: 'You must indicate your STEM OPT eligibility.',
              }}
              blockError
            />
          </div>
        </>
      )}
      {authorizationExpires && (
        <>
          <EnrollmentFormInput
            name="work_authorization_expires"
            type="date"
            label="What date does your work authorization expire?"
            placeholder="MM/DD/YYYY"
            defaultValue={previousData.work_authorization_expires}
            inputRef={register}
            error={errors.work_authorization_expires}
          />
          <EnrollmentFormError errorField={errors.work_authorization_expires} />
        </>
      )}
      <p>
        What is your employment status? <em>*</em>
      </p>
      <div className="blockInputs">
        <EnrollmentFormRadioBlockInput
          name="employment_status"
          id="full-time-status"
          label="Full Time"
          value="Full Time"
          defaultValue={previousData.employment_status}
          onClick={() => {
            setPotentialInternSeeking(false);
            triggerValidation();
          }}
          inputRef={register({ required: true })}
        />
        <EnrollmentFormRadioBlockInput
          name="employment_status"
          id="part-time-status"
          label="Part Time"
          value="Part Time"
          defaultValue={previousData.employment_status}
          onClick={() => {
            setPotentialInternSeeking(false);
            triggerValidation();
          }}
          inputRef={register({ required: true })}
        />
        <EnrollmentFormRadioBlockInput
          name="employment_status"
          id="contractor-status"
          label="Employed as Contractor"
          value="Contractor"
          defaultValue={previousData.employment_status}
          onClick={() => {
            setPotentialInternSeeking(false);
            triggerValidation();
          }}
          inputRef={register({ required: true })}
        />
        <EnrollmentFormRadioBlockInput
          name="employment_status"
          id="unemployed-status"
          label="Unemployed"
          value="Unemployed"
          defaultValue={previousData.employment_status}
          onClick={() => {
            setPotentialInternSeeking(false);
            triggerValidation();
          }}
          inputRef={register({ required: true })}
        />
        <EnrollmentFormRadioBlockInput
          name="employment_status"
          id="student-status"
          label="Student"
          value="Student"
          defaultValue={previousData.employment_status}
          onClick={() => {
            setPotentialInternSeeking(true);
            triggerValidation();
          }}
          inputRef={register({ required: true })}
        />
        <div className="emptyBlock" />
      </div>
      <EnrollmentFormError errorField={errors.employment_status} blockError />
      {potentialInternSeeking && (
        <>
          <p>
            What kind of position are you looking for? <em>*</em>
          </p>
          <div className="blockInputs">
            <EnrollmentFormRadioBlockInput
              name="seeking_position"
              id="seeking-full-time"
              label="Full-Time"
              value="Full-Time"
              defaultValue={previousData.seeking_position}
              inputRef={register({ required: true })}
              onClick={() => {
                triggerValidation('seeking_position');
              }}
            />
            <EnrollmentFormRadioBlockInput
              name="seeking_position"
              id="seeking-internship"
              label="Internship"
              value="Internship"
              defaultValue={previousData.seeking_position}
              inputRef={register({ required: true })}
              onClick={() => {
                triggerValidation('seeking_position');
              }}
            />
          </div>
          <EnrollmentFormError errorField={errors.seeking_position} blockError />
        </>
      )}
    </EnrollmentForm>
  );
};

EducationEmploymentForm.defaultProps = {
  primaryTrack: null,
};

EducationEmploymentForm.propTypes = {
  primaryTrack: PropTypes.string,
};

export default EducationEmploymentForm;
